import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { IActionMapping, ITreeOptions, KEYS, TreeComponent, TreeModel, TreeNode } from 'angular-tree-component';
import { Subscription } from 'rxjs';

import { IMCMapView } from '../../../classes/mcMapView';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { IGroup, ILayerLoadStatusChangeDetail, ILayerTypes, IMapcoreGroupLayer, IMapcoreSubLayer, MapCoreLayer } from '../../../types';
import { BaseComponent } from '../../base.component';

const limitRows = 5;
const resizeWait = 50;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-legend',
    styleUrls: ['../../sharedStyles/base.scss', '../../sharedStyles/angular-tree-component.css', './legend.component.scss'],
    templateUrl: './legend.component.html'
})
export class LegendComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    SymbolUtils: __esri.symbolUtils;

    @Input() mapViewRef: IMCMapView;
    @Output() readonly optionSelectedEvent = new EventEmitter<{ target: HTMLElement; layerId: string }>();
    @Output() readonly groupOptionSelectedEvent = new EventEmitter<{ target: HTMLElement; groupLayerId: string }>();
    @Output() public readonly onsymbolchange = new EventEmitter<{ symbolIsColorRamp: boolean; uniqueLayerId: string }>();
    @Input()
    @InputConverter()
    showLayerDetail: boolean = true;
    mapViewReadySub: Subscription;
    mapLayersSub: Subscription;
    layerList: ILayerTypes;
    layerOptions = [];
    nodes: INode[];
    options: ITreeOptions;

    @Input() @InputConverter() readonly: boolean = false;

    @ViewChild('legendTree') legendTree: TreeComponent;
    @ViewChildren('options') set itemOptions(itemOptions: QueryList<ElementRef<HTMLSpanElement>>) {
        if (this.legendOptions.nativeElement.assignedNodes().length > 0) {
            itemOptions.forEach(item => {
                const itemEl = item.nativeElement;
                itemEl.innerHTML = this.legendOptions.nativeElement.assignedNodes()[0].outerHTML;
                // value of -1 means that element is a group node and shouldn't trigger event
                if (itemEl.attributes[0].value !== '-1') {
                    for (const child of Array.from(itemEl.children)) {
                        const eventDetails = {
                            target: child,
                            layerId: itemEl.getAttribute('uniqueLayerId')
                        };
                        child.dispatchEvent(new CustomEvent('attached', { detail: eventDetails, bubbles: true, composed: true }));
                    }
                }
            });
        }
    }
    @ViewChild('legendOptions') legendOptions: ElementRef<any>;
    @ViewChildren('groupOptions') set groupOptions(groupOptions: QueryList<ElementRef<HTMLSpanElement>>) {
        if (this.legendGroupOptions.nativeElement.assignedNodes().length > 0) {
            groupOptions.forEach(item => {
                const itemEl = item.nativeElement;
                itemEl.innerHTML = this.legendGroupOptions.nativeElement.assignedNodes()[0].outerHTML;
                for (const child of Array.from(itemEl.children)) {
                    const eventDetails = {
                        target: child,
                        layerId: itemEl.getAttribute('uniqueGroupId')
                    };
                    child.dispatchEvent(new CustomEvent('attached', { detail: eventDetails, bubbles: true, composed: true }));
                }
            });
        }
    }
    @ViewChild('legendGroupOptions') legendGroupOptions: ElementRef<any>;

    get hiddenLayers() {
        return (<MapCoreLayer[]>this.layerService.mapRef.layers.toArray()).filter(l => l.mapcoreProps.hideInLegend);
    }

    constructor(_vcr: ViewContainerRef, private readonly layerService: LayerService, private readonly changeDetectorRef: ChangeDetectorRef) {
        super(_vcr);
        this.registerEvent('optionSelectedEvent', this.optionSelectedEvent);
        this.registerEvent('onsymbolchange', this.onsymbolchange);

        layerService.layersRefreshed.subscribe(() => {
            this.refreshTree();
        });
    }

    async ngOnInit() {
        this.SymbolUtils = await ESRI.SymbolUtils;

        this.options = {
            allowDrag: true,
            allowDrop: (element, to) => {
                // return true / false based on element, to.parent, to.index. e.g.
                if (element === undefined || to.index < 0) { return false; }
                return true;
            },
            actionMapping: {
                mouse: {
                    click: null,
                    dragStart: (_tree: TreeModel, _node: TreeNode, e: DragEvent) => {
                        const cancel = (event: DragEvent) => {
                            event.cancelBubble = true;
                            event.preventDefault();
                            event.stopPropagation();
                            return false;
                        };
                        const reorderHandle = (<HTMLDivElement>e.composedPath()[0]).getElementsByClassName('reorder')[0];
                        if (reorderHandle) {
                            const bounds = reorderHandle.getBoundingClientRect() as DOMRect;
                            if (!(e.x > bounds.left && e.y > bounds.top && e.x < bounds.right && e.y < bounds.bottom)) {
                                cancel(e); // Cancel if mouse isn't over the reorder handle
                            }
                        } else {
                            cancel(e); // Cancel if there is no reorder handle
                        }
                    },
                    drop: (_tree: TreeModel, toNode: TreeNode, e: DragEvent, fromTo: { from: TreeNode; to: { parent: TreeNode; index: number; dropOnNode?: boolean } }) => {
                        const layer = this.layerService.getEsriLayerByUniqueId(fromTo.from.data.id);
                        const to = fromTo.to.dropOnNode ? toNode : fromTo.to;
                        let dropPosition: 'on' | 'above' | 'below';
                        if (fromTo.to.dropOnNode) {
                            const dropTargetBounding = (<HTMLDivElement>e.currentTarget).getBoundingClientRect() as DOMRect;
                            dropPosition = (e.y - dropTargetBounding.y) < (dropTargetBounding.height / 2) ? 'above' : 'below';
                        } else {
                            dropPosition = 'on';
                        }
                        if ((dropPosition === 'above' && fromTo.from.index > to.index) || dropPosition === 'below') {
                            to.index++;
                        }
                        if (to.parent && to.parent.data.nodeType === 'group') {
                            // Is dropping on group layer
                            const groupLayer = this.layerService.getEsriLayerByUniqueId(to.parent.data.id) as IMapcoreGroupLayer;
                            if (groupLayer.layers.find(l => l.id === layer.id)) {
                                // Layer is in group
                                let toIndex = groupLayer.layers.length - to.index; // ESRI's layer order is reversed, so we need to reverse the index
                                if ((dropPosition === 'on' && fromTo.from.index > to.index) || (dropPosition === 'below' && fromTo.from.index > to.index)) {
                                    toIndex--;
                                }
                                groupLayer.layers.reorder(layer, toIndex);
                                this.refreshTree();
                            }
                        } else if (!layer.mapcoreProps.groupParent) {
                            // layer is not in a group
                            let toIndex = this.layerService.mapRef.layers.length - (to.index + this.hiddenLayers.length); // ESRI's layer order is reversed, so we need to reverse the index
                            if ((dropPosition === 'on' && fromTo.from.index > to.index) || (dropPosition === 'below' && fromTo.from.index > to.index)) {
                                toIndex--;
                            }
                            this.layerService.mapRef.reorder(layer, toIndex);
                            this.refreshTree();
                        }
                    }
                },
                keys: { // Doesn't detect input properly inside a component, have to disabled these so you can type
                    [KEYS.RIGHT]: null,
                    [KEYS.LEFT]: null,
                    [KEYS.DOWN]: null,
                    [KEYS.UP]: null,
                    [KEYS.SPACE]: null,
                    [KEYS.ENTER]: null
                }
            } as IActionMapping
        } as ITreeOptions;
        this.nodes = [];
    }

    async ngAfterViewInit() {
        this.mapViewRef = this.mapViewRef ? this.mapViewRef : await this.setupMapViewRef(this.mapViewRef);
        this.getMapLayerList();
        this.isReady();
        this.triggerLayerNameUpdates();

        window.addEventListener('layerUpdatingChange', (e: CustomEvent<ILayerLoadStatusChangeDetail>) => {
            const node = this.getNodeByESRIId(e.detail.layerId);
            if (node) {
                node.loading = e.detail.updating;
            }
        });
    }

    @Input()
    public toggleBreakDetail = (layerId: string) => {
        const node = this.findLayerInNodes(layerId, this.nodes);
        if (node) {
            this.toggleBreakDetail(node);
        }
    }
    private findLayerInNodes(layerId: string, nodes: any[]) {
        let retVal = nodes.find(n => n.data.id === layerId);
        if (!retVal) {
            const children = nodes.map(n => n.data.children as any[]).flat();
            if (children.length) {
                retVal = this.findLayerInNodes(layerId, children);
            }
        }
        return retVal;
    }

    @Input()
    public removeGroup = (groupLayerId: string) => {
        const groupLayer = this.layerService.mapRef.findLayerById(groupLayerId);
        if (groupLayer) {
            this.layerService.mapRef.remove(groupLayer);
            this.refreshTree();
        }
    }

    removeLayers(children: any[]) {
        for (const childNode of children) {
            if (childNode.children) {
                this.removeLayers(childNode.children);
            }
            this.mapViewRef.map.remove(childNode.layerData.parent);
        }
    }

    toggleLayerDetails(node) {
        node.data.showDetails = !node.data.showDetails;
        this.changeDetectorRef.detectChanges();
    }

    triggerLayerNameUpdates() {
        if (this.legendTree) {
            const element = (<any>this.legendTree.viewportComponent).elementRef.nativeElement as HTMLElement;
            setTimeout((el: HTMLElement) => {
                const textareas = Array.from(el.getElementsByTagName('textarea')).filter(e => e.classList.contains('layer-name'));
                for (const textarea of textareas) {
                    this.recalcuateRows(textarea);
                }
            }, resizeWait, element);
        }
    }

    public check(node, checked) {
        this.updateChildNodeCheckbox(node, checked);
        this.updateParentNodeCheckbox(node.realParent);
        this.changeDetectorRef.detectChanges();
        this.layerService.layersRefreshed.emit();
    }

    public updateChildNodeCheckbox(node, checked) {
        this.updateLayerVisible(node.data.id, checked);
        node.data.checked = checked;
        if (node.children) {
            node.children.forEach(child => this.updateChildNodeCheckbox(child, checked));
            this.changeDetectorRef.detectChanges();
        }
    }

    public updateParentNodeCheckbox(node) {
        if (!node || !node.data || !node.data.name) {
            return;
        }

        let allChildrenChecked = true;
        let noChildChecked = true;

        for (const child of node.children) {
            if (!child.data.checked || child.data.indeterminate) {
                allChildrenChecked = false;
            }
            if (child.data.checked) {
                noChildChecked = false;
            }
        }

        if (allChildrenChecked) {
            node.data.checked = true;
            node.data.indeterminate = false;
        } else if (noChildChecked) {
            node.data.checked = false;
            node.data.indeterminate = false;
        } else {
            node.data.checked = true;
            node.data.indeterminate = true;
        }
        this.updateLayerVisible(node.data.id, node.data.checked);
        this.updateParentNodeCheckbox(node.parent);
    }

    updateLayerVisible(layerId, visible) {
        const layer = this.layerService.getEsriLayerByUniqueId(layerId);
        if (layer) {
            layer.visible = visible;
        }
    }

    async getMapLayerList() {
        this.mapLayersSub = this.layerService.mapLayersSub.subscribe(layers => {
            if (!layers) {
                return;
            }

            this.layerList = layers;
            this.refreshTree();
        });
    }

    getLegendLayers() {
        return ((this.layerService.mapRef.layers.toArray() as MapCoreLayer[]).filter(layer => layer.mapcoreProps && !layer.mapcoreProps.hideInLegend && !layer.mapcoreProps.groupParent && !(layer.listMode === 'hide'))).slice().reverse();
    }

    @Input()
    refreshTree = (detectChangesBeforeTreeCreation: boolean = false) => {
        this.nodes = [];
        if (detectChangesBeforeTreeCreation) {
            this.changeDetectorRef.detectChanges();
        }
        const layers = this.getLegendLayers();
        for (const layer of layers) {
            if (layer.type === 'group') {
                const groupParent = layer.layers.length ? layer.layers.getItemAt(0).mapcoreProps.groupParent : <IGroup>{ groupId: layer.id, name: layer.title };
                const groupNode = this.createGroupNode(groupParent, layer.layers.some(sl => sl.visible));
                for (const childLayer of layer.layers.toArray().reverse()) {
                    this.createLeafNode(childLayer, groupNode);
                }
            } else {
                this.createLeafNode(layer, this.nodes);
            }
        }
        this.setParentNodesCheckboxBasedOnChildren(this.nodes);
        this.changeDetectorRef.detectChanges();
        this.triggerLayerNameUpdates();
    }

    createLeafNode(layer: MapCoreLayer, parentNode: INode | INode[]) {
        const mapcoreProps = layer.mapcoreProps;
        let tempLayer: MapCoreLayer | IMapcoreSubLayer;
        if (layer.type === 'map-image') {
            tempLayer = layer.sublayers.getItemAt(0);
        } else {
            tempLayer = layer;
        }
        const leafNode = {
            checked: layer.visible,
            id: layer.id,
            mapcoreId: mapcoreProps.id,
            name: mapcoreProps.name,
            nodeType: 'leaf',
            hasDetails: true,
            showDetails: false,
            showBreakDetail: false,
            layerData: tempLayer
        } as INode;

        if ((layer.loadStatus === 'loading' || layer.loadStatus === 'not-loaded') && layer.type !== 'feature') {
            leafNode.loading = true;
        }

        if (layer.type === 'graphics') {
            leafNode.hasDetails = false;
        }
        if (!Array.isArray(parentNode)) {
            parentNode.children.push(leafNode);
        } else {
            parentNode.push(leafNode);
        }
    }

    createGroupNode(groupData: IGroup, visible: boolean) {
        const groupNode = {
            checked: visible,
            children: [],
            id: groupData.groupId,
            name: groupData.name,
            nodeType: 'group',
            isExpanded: groupData.isOpen
        } as INode;
        this.nodes.push(groupNode);
        return groupNode;
    }

    addChildToGroup(layer: any, parentGroup: any) {
        this.createLeafNode(layer, parentGroup);
    }

    setParentNodesCheckboxBasedOnChildren(nodes: INode[]) {
        for (const node of nodes) {
            if (node.children) {
                this.setParentNodesCheckboxBasedOnChildren(node.children);
                node.checked = node.children.some(x => x.checked);
                node.indeterminate = node.checked && node.children.some(x => !x.checked);
            }
        }
    }

    setExpanded() {
        if (this.legendTree && this.legendTree.treeModel) {
            for (const node of this.legendTree.treeModel.nodes.filter(n => n.nodeType === 'group' && n.isExpanded) as TreeNode[]) {
                const groupRoot = this.legendTree.treeModel.roots.find(root => root.id === node.id);
                if (groupRoot) {
                    groupRoot.expand();
                }
            }
        }
    }

    toggleExpanded(e: { eventName: string; node: TreeNode; isExpanded: boolean; treeModel: TreeModel }) {
        if (e.isExpanded) {
            this.triggerLayerNameUpdates();
        }
        const layer = this.layerService.getEsriLayerByUniqueId(e.node.data.id) as IMapcoreGroupLayer;
        if (layer && layer.mapcoreProps) {
            for (const childLayer of layer.layers) {
                childLayer.mapcoreProps.groupParent.isOpen = e.isExpanded;
            }
        }
    }

    optionSelected(target: HTMLElement, layerId: string) {
        this.optionSelectedEvent.emit({ target, layerId });
    }

    groupOptionSelected(target: HTMLElement, groupLayerId: string) {
        this.groupOptionSelectedEvent.emit({ target, groupLayerId });
    }

    ngOnDestroy() {
        if (this.mapViewReadySub) {
            this.mapViewReadySub.unsubscribe();
        }

        if (this.mapLayersSub) {
            this.mapLayersSub.unsubscribe();
        }
    }

    layerNameChange(event: Event, layerId: string) {
        const element = event.target as HTMLTextAreaElement;
        const updatedName = element.value;
        const layer = this.layerService.getEsriLayerByUniqueId(layerId);
        layer.mapcoreProps.name = updatedName;
        this.recalcuateRows(element);
        this.layerService.layersRefreshed.emit();
    }

    recalcuateRows(textarea: HTMLTextAreaElement) {
        if (textarea.style.height) { return; } // Height was changed specifically, so don't recalculate
        textarea.setAttribute('rows', '1');
        while (textarea.scrollHeight > textarea.offsetHeight && parseFloat(textarea.getAttribute('rows')) < limitRows) {
            textarea.setAttribute('rows', (parseFloat(textarea.getAttribute('rows')) + 1).toString());
        }
    }

    onSymbolChange(event) {
        this.onsymbolchange.emit(event);
    }

    getNodeByESRIId(id: string, baseNode?: INode) {
        let retVal: INode;
        let nodes = this.nodes;
        if (baseNode) {
            nodes = baseNode.children || [];
        }
        for (const node of nodes) {
            if (node.nodeType === 'group') {
                retVal = this.getNodeByESRIId(id, node);
            } else if (node.id === id) {
                retVal = node;
                break;
            }
        }
        return retVal;
    }

    @Input()
    public setHorizontalFormat = () => {

        const treeElement = this.getElementByIdWithShadowRoots('legendTree');
        const style = document.createElement('style');
        //set format to horizontal flex layout, remove the reorder icons, prevent text wrapping
        style.textContent = `tree-root tree-viewport > div .angular-tree-component > tree-node-collection > div {
                                display: flex; /* Example style to make the element horizontal */
                                flex-direction: row;
                                align-items: center;
                            }
                            .legend-box .reorder {
                                display: none;
                            }
                            .legend-box .content {
                                margin-left: 0px;
                                margin-right: 5px;
                                display: flex;
                                flex-wrap: nowrap;
                                align-items: center;
                                flex-grow: 1;
                                position: relative;
                            }
                            .layer-name {
                                width: auto;
                                border: none;
                                margin: 0;
                                padding: 2px 17px 2px 2px;
                                background: transparent;
                                box-shadow: none;
                                line-height: 19px;
                                flex: 1 1 0;
                                resize: vertical;
                                min-height: 19px;
                                white-space: nowrap;
                            }
                            tree-node-drop-slot {
                                display:none;
                            }
                            tree-root tree-viewport > div .angular-tree-component > tree-node-collection > div > tree-node > div > tree-node-wrapper > .node-wrapper {
                                display: flex;
                                align-items: center;
                            }
                            .tree-node {
                                border-bottom: none
                            }
                            .angular-tree-component {
                                cursor: auto;
                                line-height: 12px;
                            }
                            tree-node-expander {
                                width: 10px;
                                align-self: center;
                                line-height: 0;
                            }
                            `;

        if (treeElement) {
            treeElement.appendChild(style);
        }
    }

    private getElementByIdWithShadowRoots(id: string): HTMLElement | null {
        // Check the main document first
        const element = document.getElementById(id);
        if (element) { return element; }

        // If not found in the main document, search through all shadow roots in the document
        const shadowHosts = Array.from(document.querySelectorAll<HTMLElement>('mc-legend')); // Adjust with correct selector
        for (const host of shadowHosts) {
            if (host.shadowRoot) {
                // Search within the shadow root
                const foundElement = host.shadowRoot.querySelector<HTMLElement>(`#${id}`);
                if (foundElement) { return foundElement; }
            }
        }

        return null;
    }

    @Input()
    public collapseAll = () => {
        const treeElement = this.getElementByIdWithShadowRoots('legendTree');
        const expandedLegendItems = treeElement.querySelectorAll('.toggle-children-wrapper-expanded');
        expandedLegendItems.forEach(item => {
            (item as HTMLElement).click();
        });
    }

}

interface INode {
    checked: boolean;
    children?: INode[];
    id: string;
    mapcoreId?: number;
    name: string;
    nodeType: 'leaf' | 'group';
    hasDetails?: boolean;
    showDetails?: boolean;
    showBreakDetail?: boolean;
    layerData?: MapCoreLayer | IMapcoreSubLayer;
    indeterminate?: boolean;
    isExpanded?: boolean;
    loading?: boolean;
}
